import { RFQ_INVITE_STATUS_LABELS, SERVICE_TYPE_LABELS, type RfqSupplierListItem } from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Badge } from '@caterinx/ui/components/ui/badge';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, MapPin, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchMyRfqs, formatDate, formatDeadline, formatRemaining } from '../lib/rfq.js';

/**
 * `/ausschreibungen` — die Ausschreibungen dieses Betriebs.
 *
 * ### Wonach die Liste sortiert ist und warum
 *
 * Nach **Fristende**, nicht nach Eingang. In einem Sechs-Stunden-Fenster ist die Frage nicht
 * „was ist neu", sondern „was läuft mir gleich weg". Die Sortierung kommt vom Server (GSI1 mit
 * `INV#<bidsCloseAt>` absteigend); die Oberfläche gruppiert nur noch in „offen" und „erledigt".
 *
 * ### Was hier nicht steht
 *
 * Keine Zahl über Mitbewerber, kein Kundenname, keine Straße. Das ist keine Zurückhaltung
 * der Oberfläche, sondern das Schema: `RfqSupplierListItem` kennt diese Felder nicht (§5).
 */
export function SupplierRfqsPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['supplier-rfqs'],
    queryFn: fetchMyRfqs,
    /**
     * Eine Minute. Die Frist ist der einzige Wert, der sich hier von selbst ändert, und der
     * Countdown unten rechnet ohnehin lokal weiter — häufigeres Nachladen brauchte niemand
     * und würde nur das Rate-Limit belasten.
     */
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];
  const open = items.filter((item) => item.status === 'broadcast' && item.inviteStatus !== 'declined');
  const done = items.filter((item) => item.status !== 'broadcast' || item.inviteStatus === 'declined');

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Ausschreibungen</h1>
          <p className="text-muted-foreground text-sm">Anfragen, zu denen Sie eingeladen wurden</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/">Zum Portal</Link>
        </Button>
      </header>

      {isPending && (
        <>
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Liste konnte nicht geladen werden</AlertTitle>
          <AlertDescription>Bitte laden Sie die Seite neu.</AlertDescription>
        </Alert>
      )}

      {data && items.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Noch keine Ausschreibungen</CardTitle>
            <CardDescription>
              Sobald eine Anfrage zu Ihrem Liefergebiet, Ihrer Gästezahl und Ihren Leistungen passt, erhalten Sie eine
              E-Mail — und sie erscheint hier. Ein vollständiges Profil mit gepflegten Liefergebieten erhöht die Zahl
              der passenden Anfragen deutlich.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {open.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            Offen — Angebot möglich
          </h2>
          {open.map((item) => (
            <RfqCard key={item.rfqId} item={item} />
          ))}
        </section>
      )}

      {done.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Erledigt</h2>
          {done.map((item) => (
            <RfqCard key={item.rfqId} item={item} muted />
          ))}
        </section>
      )}
    </main>
  );
}

/* eslint-disable security/detect-object-injection -- Enum-Werte, serverseitig gegen Zod validiert */

function RfqCard({ item, muted = false }: { item: RfqSupplierListItem; muted?: boolean }) {
  const isOpen = item.status === 'broadcast' && item.inviteStatus !== 'declined';

  return (
    <Link
      to={`/ausschreibung/${item.rfqId}`}
      className="hover:border-primary focus-visible:ring-ring block rounded-lg border p-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className={muted ? 'text-muted-foreground font-medium' : 'font-medium'}>{item.title}</h3>
        <StatusBadge item={item} />
      </div>

      <dl className="text-muted-foreground mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
        <div className="flex items-center gap-1.5">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <dd>
            {formatDate(item.eventDate)}, {item.eventStartTime} Uhr
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="h-4 w-4" aria-hidden="true" />
          <dd>{item.guestCount} Personen</dd>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin className="h-4 w-4" aria-hidden="true" />
          <dd>
            {item.postalCode} {item.city}
          </dd>
        </div>
      </dl>

      <p className="text-muted-foreground mt-2 text-sm">
        {item.serviceTypes.map((type) => SERVICE_TYPE_LABELS[type]).join(', ')}
      </p>

      {isOpen && item.bidsCloseAt && (
        /**
         * Frist immer doppelt: Restzeit **und** konkreter Zeitpunkt (D12). Die Restzeit sagt,
         * wie dringend es ist; der Zeitpunkt sagt, ob es sich heute Abend noch ausgeht.
         */
        <p className="mt-3 text-sm font-medium">
          {formatRemaining(item.bidsCloseAt)}{' '}
          <span className="text-muted-foreground font-normal">— bis {formatDeadline(item.bidsCloseAt)}</span>
        </p>
      )}
    </Link>
  );
}

function StatusBadge({ item }: { item: RfqSupplierListItem }) {
  if (item.inviteStatus === 'declined') return <Badge variant="outline">Abgesagt</Badge>;
  if (item.hasOffer) return <Badge>Angebot abgegeben</Badge>;
  if (item.status !== 'broadcast') return <Badge variant="secondary">Frist abgelaufen</Badge>;
  return <Badge variant="secondary">{RFQ_INVITE_STATUS_LABELS[item.inviteStatus]}</Badge>;
}

/* eslint-enable security/detect-object-injection */
